import { DateTime } from "luxon";
import { FC, useCallback, useContext, useMemo } from "react";
import { useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";

import { Dropdown, Form } from "capitalrx-components-2.0/lib/forms";
import { Panel, Tag, useAlert } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Body, Paragraph, Semibold, Subheading } from "capitalrx-components-2.0/lib/typography";

import {
  Authenticator,
  useGetAccountPreferencesQuery,
  useListAuthenticatorsQuery,
  useListValidAuthenticatorsQuery,
} from "@/App/api/authenticators";
import { updateMemberNotificationSettings } from "@/App/api/member";
import { useGetUserPreferencesQuery, useUpdateUserPreferencesMutation } from "@/App/api/preferences";
import {
  ContentPanel,
  PageWrapper,
  ThemedButton,
  ThemedCheckbox,
  ThemedSubmitButton,
  ThemedTabbedContainer,
} from "@/App/components";
import { AuthenticationContext } from "@/App/context";
import { MemberEligibilityContext } from "@/App/context/MemberEligibilityContext";
import { useNavigateClient } from "@/App/hooks";

interface PreferencesForm {
  readonly opt_in_to_emails: boolean;
  readonly selected_notification_authenticator_id: number;
  readonly opt_in_to_push_notifications: boolean;
}

export const PreferencesPage: FC = () => {
  const { i18n, t: translate } = useTranslation();

  const [searchParams, setSearchParams] = useSearchParams();
  const { memberEligibility, isViewingDataholder } = useContext(MemberEligibilityContext);
  const { getAuthToken } = useContext(AuthenticationContext);

  const isEmulating = sessionStorage.getItem("isEmulation") === "true";
  const alert = useAlert();
  const navigate = useNavigateClient();

  const { data: preferences } = useGetAccountPreferencesQuery();
  const { data: appUserPreferences, refetch: refetchAppUserPreferences } = useGetUserPreferencesQuery();
  const { data: authenticators } = useListAuthenticatorsQuery();
  const { data: validAuthenticators } = useListValidAuthenticatorsQuery();
  const { mutateAsync: updateAppUserPreferences } = useUpdateUserPreferencesMutation();

  const allowOptional = preferences && preferences.allow_optional;

  const nativeAuthenticatorPresent = useCallback(() => {
    if (authenticators) {
      for (let index = 0; index < authenticators.results.length; index++) {
        const authenticator = authenticators.results[index];
        if (authenticator.authenticator === "aws/cognito") {
          return true;
        }
      }
    }
    return false;
  }, [authenticators]);

  const preferencesForm = useForm<PreferencesForm>({
    defaultValues: {
      opt_in_to_emails: memberEligibility.member.opt_in_to_emails,
      opt_in_to_push_notifications: memberEligibility.member.opt_in_to_push_notifications,
      selected_notification_authenticator_id: memberEligibility.member.selected_notification_authenticator_id,
    },
  });

  useMemo(() => {
    if (Array.isArray(validAuthenticators?.authenticators) && validAuthenticators.authenticators.length > 0) {
      preferencesForm.setValue("opt_in_to_emails", memberEligibility.member.opt_in_to_emails);
      if (!memberEligibility.member.selected_notification_authenticator_id) {
        let flag = false;
        for (let index = 0; index < validAuthenticators.authenticators.length; index++) {
          const validAuthenticator = validAuthenticators.authenticators[index];
          if (validAuthenticator.authenticator === "aws/cognito") {
            preferencesForm.setValue("selected_notification_authenticator_id", validAuthenticator.id);
            flag = true;
          }
        }
        if (!flag) {
          preferencesForm.setValue("selected_notification_authenticator_id", validAuthenticators.authenticators[0].id);
        }
      }
    } else {
      preferencesForm.setValue("opt_in_to_emails", false);
    }
  }, [memberEligibility, validAuthenticators]);

  /* TODO: get the member/dataholder_list call to return back the below data for PHI viewer mode to work:
  opt_in_to_emails, opt_in_to_push_notifications, and selected_notification_authenticator_id */
  const optIntoEmailsFormWatch = preferencesForm?.watch("opt_in_to_emails");

  const onSubmit = useCallback(async (formData: PreferencesForm) => {
    if (formData.opt_in_to_emails && !formData.selected_notification_authenticator_id) {
      alert({
        type: "error",
        style: "toast",
        message: translate("PREFERENCES_PAGE.mustSelectAnEmail"),
      });
    } else {
      try {
        await updateMemberNotificationSettings(await getAuthToken(), formData);
        alert({
          type: "success",
          style: "toast",
          message: translate("PREFERENCES_PAGE.notificationSettingsUpdated"),
        });
      } catch {
        alert({
          type: "error",
          style: "toast",
          message: translate("PREFERENCES_PAGE.unableToUpdate"),
        });
      }
    }
  }, []);

  const languageDropdownChangeHandler = async (value: string) => {
    try {
      const selectedLanguage = value?.toString() || null;
      await updateAppUserPreferences({ language: selectedLanguage });
      refetchAppUserPreferences();
      if (selectedLanguage && selectedLanguage !== i18n.language) {
        i18n.changeLanguage(selectedLanguage);
      }
      alert({
        type: "success",
        style: "toast",
        message: translate("PREFERENCES_PAGE.languageSettingsUpdateSuccess"),
      });
    } catch {
      alert({
        type: "error",
        style: "toast",
        message: translate("PREFERENCES_PAGE.languageSettingsUpdateError"),
      });
    }
  };

  const handleNewLogin = useCallback(() => {
    navigate("/add-native-authentication");
  }, []);

  const handleTabChange = useCallback(
    (activeTab: string) => {
      setSearchParams({
        ...searchParams,
        viewTab: activeTab,
      });
    },
    [searchParams],
  );

  const allowAdd = useCallback(() => {
    if (allowOptional) {
      if (authenticators && authenticators.results.filter((result) => result.authenticator === "aws/cognito").length === 0) {
        return true;
      }
    }
    return false;
  }, [authenticators, preferences]);

  const allowNotificationsTab = useCallback(() => {
    if (allowOptional) {
      return true;
    } else if (validAuthenticators) {
      return true;
    }
    return false;
  }, [validAuthenticators, allowOptional]);

  const desktopView = useCallback((result: Authenticator) => {
    const registration = result.authenticator === "aws/cognito" ? "Native" : "SSO";
    return (
      <Panel key={result.id.toString()}>
        <Stack alignment="left" spacing="small">
          <Shelf alignment="bottom" spacing="small">
            <Subheading>{registration} {translate("PREFERENCES_PAGE.registration")}</Subheading>
            {preferences && result.identity === preferences.identity && <Tag type="info-high-contrast">{translate("PREFERENCES_PAGE.currentSession")}</Tag>}
          </Shelf>
          <Shelf alignment="bottom" spacing="mini">
            <Semibold>{translate("PREFERENCES_PAGE.identity")}:</Semibold>
            {result.identity}
            <Shelf.Spacer />
            <Semibold>{translate("PREFERENCES_PAGE.creationDate")}:</Semibold>
            {DateTime.fromISO(result.created_at).toLocaleString(DateTime.DATETIME_FULL)}
          </Shelf>
        </Stack>
      </Panel>
    );
  }, []);

  const mobileView = useCallback((result: Authenticator) => {
    const registration = result.authenticator === "aws/cognito" ? "Native" : "SSO";
    return (
      <Panel key={result.id.toString()}>
        <Stack alignment="left" spacing="small">
          <Shelf alignment="bottom" spacing="small">
            <Subheading>{registration} {translate("PREFERENCES_PAGE.registration")}</Subheading>
            {preferences && result.identity === preferences.identity && <Tag type="info-high-contrast">{translate("PREFERENCES_PAGE.currentSession")}</Tag>}
          </Shelf>
          <Shelf alignment="bottom" spacing="mini">
            <Semibold>{translate("PREFERENCES_PAGE.identity")}:</Semibold>
            {result.identity}
          </Shelf>
          <Shelf alignment="bottom" spacing="mini">
            <Semibold>{translate("PREFERENCES_PAGE.creationDate")}:</Semibold>
            {DateTime.fromISO(result.created_at).toLocaleString(DateTime.DATETIME_FULL)}
          </Shelf>
        </Stack>
      </Panel>
    );
  }, []);

  const { renderAuthPanelFunction, languageFieldCols } = useResponsive({
    renderAuthPanelFunction: {
      mobile: mobileView,
      desktop: desktopView,
    },
    languageFieldCols: {
      mobile: 12,
      tablet: 6,
      desktop: 3,
    },
  });

  return (
    <PageWrapper>
      <ContentPanel title={translate("PREFERENCES_PAGE.preferences")}>
        <ThemedTabbedContainer
          testId="preferences-tab-container"
          selectedTab={searchParams.get("viewTab")}
          onSelectedTabChange={handleTabChange}
        >
          {allowNotificationsTab() && (
            <ThemedTabbedContainer.TabPane testId="notification-preferences" id="notification-preferences" label={translate("PREFERENCES_PAGE.notifications")}>
              <Subheading testId="notification-subheading">{translate("PREFERENCES_PAGE.notificationSettings")}</Subheading>
              <Form form={preferencesForm} readOnly={isViewingDataholder || isEmulating} onSubmit={onSubmit} testId="notifications-form">
                <Stack>
                  <ThemedCheckbox.Field
                    testId="opt-into-emails"
                    hideLabel
                    disabled={
                      !isEmulating
                      && !isViewingDataholder
                      && Array.isArray(validAuthenticators?.authenticators)
                      && validAuthenticators.authenticators.length > 0? false: true
                    }
                    name="opt_in_to_emails"
                    label={translate("PREFERENCES_PAGE.optIntoEmails")}
                    instructions={translate("PREFERENCES_PAGE.optIntoEmailsInstructions")}
                  />
                  {optIntoEmailsFormWatch?.valueOf() && (
                    <Dropdown.Field
                      testId="selected-notification-authenticator-dropdown"
                      label={translate("PREFERENCES_PAGE.sendEmailsTo")}
                      name="selected_notification_authenticator_id"
                      required={true}
                      placeholder={translate("PREFERENCES_PAGE.selectEmail")}
                      instructions={
                        !nativeAuthenticatorPresent() && (
                          <Paragraph style={{ marginBottom: -16 }}>
                            <Trans
                              i18nKey="PREFERENCES_PAGE.createNewLogin"
                              components={{
                                link: (
                                  <a
                                    href="#account-preferences"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      setSearchParams({ ...searchParams, viewTab: "account-preferences" });
                                    }}
                                    style={{ fontWeight: 600, textDecoration: "underline", cursor: "pointer" }}
                                  />
                                ),
                              }}
                            />
                          </Paragraph>
                        )
                      }
                    >
                      {validAuthenticators
                        && validAuthenticators.authenticators
                        && validAuthenticators.authenticators.map((validAuthenticator: Authenticator) => {
                          return (
                            <Dropdown.Option key={validAuthenticator.id} value={validAuthenticator.id}>
                              {validAuthenticator.identity}
                            </Dropdown.Option>
                          );
                        })}
                    </Dropdown.Field>
                  )}
                  <ThemedCheckbox.Field
                    testId="opt-into-push-notifications"
                    hideLabel
                    disabled={isEmulating || isViewingDataholder}
                    name="opt_in_to_push_notifications"
                    label={translate("PREFERENCES_PAGE.optIntoPushNotificacions")}
                    instructions={translate("PREFERENCES_PAGE.optIntoPushNotificacionsInstructions")}
                  />
                  <Shelf alignment="bottom">
                    {!isEmulating && !isViewingDataholder && <ThemedSubmitButton>{translate("PREFERENCES_PAGE.save")}</ThemedSubmitButton>}
                    <Shelf.Spacer />
                  </Shelf>
                </Stack>
              </Form>
            </ThemedTabbedContainer.TabPane>
          )}
          {preferences && !isViewingDataholder && (
            <ThemedTabbedContainer.TabPane testId="account-preferences" id="account-preferences" label={translate("PREFERENCES_PAGE.accounts")}>
              <Stack alignment="span" spacing="medium">
                <Shelf alignment="top">
                  <Subheading testId="account-subheading">{translate("PREFERENCES_PAGE.allLogins")}</Subheading>
                  <Shelf.Spacer />
                  {allowAdd() && (
                    <ThemedButton appearance="text" icon="add" disabled={isEmulating || isViewingDataholder} onClick={handleNewLogin}>
                      {translate("PREFERENCES_PAGE.newLogin")}
                    </ThemedButton>
                  )}
                </Shelf>
                <Stack alignment="span" spacing="small">
                  {authenticators
                    && authenticators.results.map((result: Authenticator) => {
                      return renderAuthPanelFunction(result);
                    })}
                </Stack>
              </Stack>
            </ThemedTabbedContainer.TabPane>
          )}
          {!isViewingDataholder && (
            <ThemedTabbedContainer.TabPane
              testId="app-user-language-preferences"
              id="app-user-language-preferences"
              label={translate("PREFERENCES_PAGE.language")}
            >
              <Stack>
                <Body>{translate("PREFERENCES_PAGE.languageSettingsDescription")}</Body>
                <Grid>
                  <Grid.Span columns={languageFieldCols}>
                    <Dropdown
                      testId="selected-language-preferences"
                      label={translate("PREFERENCES_PAGE.preferredLanguage")}
                      placeholder={translate("PREFERENCES_PAGE.select")}
                      clearable={true}
                      value={appUserPreferences?.language}
                      onChange={languageDropdownChangeHandler}
                    >
                      <Dropdown.Option value="en">English</Dropdown.Option>
                      <Dropdown.Option value="es">Español</Dropdown.Option>
                    </Dropdown>
                  </Grid.Span>
                </Grid>
              </Stack>
            </ThemedTabbedContainer.TabPane>
          )}
        </ThemedTabbedContainer>
      </ContentPanel>
    </PageWrapper>
  );
};
